import {
  Box,
  Button,
  Collapse,
  Icon,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
  visuallyHidden,
} from "@outschool/backpack";
import { isLiveOneTime } from "@outschool/business-rules";
import { ClassDetailsQuery_SectionFragment } from "@outschool/gql-frontend-generated";
import {
  faChevronDown,
  faChevronUp,
  faDiamondExclamation,
} from "@outschool/icons";
import { TFunction, useLocale, useTranslation } from "@outschool/localization";
import * as Time from "@outschool/time";
import { useSectionListViewed } from "@outschool/ui-analytics";
import lodashRange from "lodash/range";
import React, { useCallback, useState } from "react";

import { getMeetingsForDisplay } from "../../utils/Section";
import { UnscheduledMeetingsNotice } from "./DesktopSectionTable";
import LateEnrollmentMeetingListDescription from "./LateEnrollmentMeetingListDescription";
import {
  SectionRowActivity,
  SectionRowSection,
  SectionTableCta,
} from "./types";

export const MOBILE_ROWS_PER_PAGE = 4;

interface MobileSectionTableProps {
  isLoading: boolean;
  activity: SectionRowActivity;
  sections: SectionRowSection[];
  totalCount: number;
  fetchNextPage: (offset: number) => void;
  userTimeZone: string;
  isSellerOrg: boolean;
  CtaComponent: SectionTableCta;
}

function useTableRowSx({
  isExpandingRow,
  isSellerOrg,
}: {
  isExpandingRow?: boolean;
  isCondensedTabletView?: boolean;
  isSellerOrg?: boolean;
}) {
  return React.useMemo(() => {
    // standardize row height to prevent UI jumping between the loading state and populated table
    // allow more room for seller org teacher names
    // for rows that expand, bottom padding comes from the expanded row, so we subtract that amount
    let rowHeight = isSellerOrg ? "149px" : "125px";
    if (isExpandingRow) {
      rowHeight = `calc(${rowHeight} - 16px)`;
    }
    return {
      "th, td": {
        height: rowHeight,
        width: "50%",
        verticalAlign: "middle",
        border: "none",
        paddingBottom: isExpandingRow ? 0 : "16px",
      },
      "&:not(:first-of-type)": {
        borderTop: "1px solid",
        borderTopColor: "neutral.200",
      },
    };
  }, [isExpandingRow, isSellerOrg]);
}

function LoadingRow({ isSellerOrg }: { isSellerOrg?: boolean }) {
  const tableRowSx = useTableRowSx({ isSellerOrg });
  return (
    <TableRow sx={tableRowSx}>
      <TableCell>
        <Box sx={{ width: "100%" }}>
          <Skeleton height={120} width={"100%"} />
        </Box>
      </TableCell>
    </TableRow>
  );
}

function LoadingPagination() {
  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        justifyContent: "flex-end",
        height: 52,
        alignItems: "center",
      }}
    >
      <Skeleton
        height={40}
        width={140}
        sx={{
          marginRight: "1em",
          paddingTop: "16px",
          paddingBottom: "16px",
        }}
      />
    </Box>
  );
}

export default function MobileSectionTable({
  isLoading,
  activity,
  sections,
  fetchNextPage,
  totalCount,
  isSellerOrg,
  userTimeZone,
  CtaComponent,
}: MobileSectionTableProps) {
  const locale = useLocale();
  const trackSectionListViewed = useSectionListViewed();
  const [page, setPage] = React.useState(0);

  function handleChangePage(_event: any, newPage: number) {
    setPage(newPage);
    fetchNextPage(newPage * MOBILE_ROWS_PER_PAGE);
  }

  function pageLabel({ count, page }: { count: number; page: number }) {
    return `Page ${page + 1} of ${Math.ceil(count / MOBILE_ROWS_PER_PAGE)}`;
  }

  const [expandedSectionUid, setExpandedSectionUid] = useState(null);
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\MobileSectionTable"
  );
  const showTablePagination = totalCount > MOBILE_ROWS_PER_PAGE;

  const SectionRow = isLiveOneTime(activity)
    ? OneTimeSectionRow
    : MultiWeekSectionRow;

  if (!isLoading) {
    trackSectionListViewed({
      component: "MobileSectionTable",
      page,
      rowsPerPage: MOBILE_ROWS_PER_PAGE,
      sections,
      totalCount,
    });
  }

  return (
    <>
      <TableContainer
        sx={{
          borderBottom: "1px solid",
          borderBottomColor: "neutral.200",
          backgroundColor: "common.white",
        }}
      >
        <Table>
          <caption style={visuallyHidden}>
            {t("Table of available sections")}
          </caption>
          <TableBody>
            {isLoading
              ? lodashRange(0, 4).map(i => (
                  <LoadingRow key={`row-${i}`} isSellerOrg={isSellerOrg} />
                ))
              : sections.map(section => (
                  <SectionRow
                    activity={activity}
                    section={section}
                    userTimeZone={userTimeZone}
                    locale={locale}
                    isSellerOrg={isSellerOrg}
                    isExpanded={expandedSectionUid === section.uid}
                    setExpandedSectionUid={setExpandedSectionUid}
                    key={section.uid}
                    CtaComponent={CtaComponent}
                    t={t}
                  />
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      {isLoading ? (
        <LoadingPagination />
      ) : (
        showTablePagination && (
          <TablePagination
            component="div"
            rowsPerPage={MOBILE_ROWS_PER_PAGE}
            rowsPerPageOptions={[]}
            labelRowsPerPage=""
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            labelDisplayedRows={pageLabel}
            sx={{ marginRight: "0.5em" }}
          />
        )
      )}
    </>
  );
}

interface SectionRowProps {
  activity: SectionRowActivity;
  section: SectionRowSection;
  userTimeZone: string;
  locale: string;
  isSellerOrg: boolean;
  isExpanded: boolean;
  setExpandedSectionUid: React.Dispatch<React.SetStateAction<string | null>>;
  CtaComponent: SectionTableCta;
  t: TFunction;
}

function MultiWeekSectionRow({
  activity,
  section,
  isSellerOrg,
  userTimeZone,
  locale,
  setExpandedSectionUid,
  isExpanded,
  t,
  CtaComponent,
}: SectionRowProps) {
  const onExpand = useCallback(() => {
    return setExpandedSectionUid(isExpanded ? null : section.uid);
  }, [setExpandedSectionUid, isExpanded, section.uid]);
  const isOngoing = !!activity.is_ongoing_weekly;
  const meetings = getMeetingsForDisplay(isOngoing, section);

  const meetingDays = Time.getDaysOfWeek({
    dateTimes: meetings.map(m => m.start_time),
    isOneTime: false,
    timeZone: userTimeZone,
    locale,
  });

  const numMeetings = meetings.length;
  const startTime = isOngoing
    ? section.nextOngoingMeeting?.start_time
    : meetings[0].start_time;
  const endTime = isOngoing ? null : meetings[numMeetings - 1].end_time;
  const tableRowSx = useTableRowSx({ isExpandingRow: true, isSellerOrg });

  return (
    <>
      <TableRow sx={tableRowSx}>
        <TableCell>
          <Typography variant="h6" data-testid="mobile-str-meeting-date">
            {meetingDays}
          </Typography>
          <Typography variant="body1" data-testid="mobile-str-meeting-time">
            {Time.briefDurationTimesString(
              meetings[0].start_time,
              meetings[0].end_time,
              userTimeZone,
              false
            )}
          </Typography>
          {!!section?.allowsLateEnrollment && !!section?.progress ? (
            <Box>
              <Typography
                variant="subtitle2"
                data-testid="mobile-late-enrollment-warning"
                sx={{
                  color: "error.700",
                  py: 4,
                  verticalAlign: "text-top",
                }}
              >
                <Icon icon={faDiamondExclamation} sx={{ marginRight: 4 }} />
                {t("Started: ")}{" "}
                {Time.formatDate(section.start_time, userTimeZone)}
              </Typography>
              <Typography
                component="div"
                variant="body1"
                data-testid="mobile-str-date-summary-start"
              >
                {t("Upcoming:")} {Time.formatDate(startTime, userTimeZone)}{" "}
                {t("(Week {{currentWeek}})", {
                  currentWeek: section?.progress?.currentWeek,
                })}
              </Typography>
            </Box>
          ) : (
            <Typography
              component="div"
              variant="body1"
              data-testid="mobile-str-date-summary-start"
            >
              {isOngoing ? t("Next meeting:") : t("Starts:")}{" "}
              {Time.formatDate(startTime, userTimeZone)}
            </Typography>
          )}

          {endTime && (
            <Typography
              component="div"
              variant="body1"
              data-testid="mobile-str-date-summary-end"
              sx={{ paddingBottom: 4 }}
            >
              {t("Ends:")} {Time.formatDate(endTime, userTimeZone)}
            </Typography>
          )}
          <TaughtBy
            section={section}
            isSellerOrg={isSellerOrg}
            isTeacherAssigned={activity.user_uid !== section.leader.uid}
            t={t}
          />
        </TableCell>
        <TableCell sx={{ textAlign: "right" }}>
          <Seats section={section} t={t} />
          <Button
            onClick={onExpand}
            variant="link"
            endIcon={
              <Icon
                icon={isExpanded ? faChevronUp : faChevronDown}
                sx={{
                  fontSize: "0.6em !important",
                }}
              />
            }
            sx={{
              color: "primary.800",
              textDecoration: "none !important",
            }}
          >
            <Typography variant="body1">
              {isExpanded
                ? t("Hide {{numMeetings}} meets", { numMeetings })
                : t("Show {{numMeetings}} meets", { numMeetings })}
            </Typography>
          </Button>
        </TableCell>
      </TableRow>
      <TableRow sx={{ td: { padding: "0", backgroundColor: "grey.50" } }}>
        <TableCell colSpan={2} sx={{ border: "none" }}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            {!!section?.progress && (
              <LateEnrollmentMeetingListDescription
                section={section}
                t={t}
                sx={{ py: 6 }}
              />
            )}

            {meetings.map((meeting: any) => (
              <Typography variant="body1" component="p" key={meeting.uid}>
                {Time.fullDurationStringWithTimes(
                  meeting.start_time,
                  meeting.end_time,
                  userTimeZone,
                  true
                )}
              </Typography>
            ))}
            <UnscheduledMeetingsNotice
              meetings={meetings}
              section={section}
              t={t}
            />
          </Collapse>
        </TableCell>
      </TableRow>
      <CTARow CtaComponent={CtaComponent} section={section} />
    </>
  );
}

function OneTimeSectionRow({
  activity,
  section,
  isSellerOrg,
  userTimeZone,
  t,
  CtaComponent,
}: SectionRowProps) {
  const meeting = section.meetings[0];

  return (
    <>
      <TableRow
        sx={{
          td: {
            height: isSellerOrg ? "100px" : "75px",
            border: "none",
            width: "50%",
            pb: 0,
          },
        }}
      >
        <TableCell>
          <Typography variant="h6" data-testid="mobile-str-meeting-date">
            {Time.formatDateWithWeekday(meeting.start_time, userTimeZone)}
          </Typography>
          <Typography variant="body1" data-testid="mobile-str-meeting-time">
            {Time.briefDurationTimesString(
              meeting.start_time,
              meeting.end_time,
              userTimeZone,
              false
            )}
          </Typography>
          <TaughtBy
            section={section}
            isSellerOrg={isSellerOrg}
            isTeacherAssigned={activity.user_uid !== section.leader.uid}
            t={t}
          />
        </TableCell>
        <TableCell sx={{ textAlign: "right" }}>
          <Seats section={section} t={t} />
        </TableCell>
      </TableRow>
      <CTARow CtaComponent={CtaComponent} section={section} />
    </>
  );
}

function TaughtBy({
  section,
  isSellerOrg,
  isTeacherAssigned,
  t,
}: {
  section: SectionRowSection;
  isSellerOrg: boolean;
  isTeacherAssigned: boolean;
  t: TFunction;
}) {
  if (isSellerOrg) {
    const sectionLeaderName = section.leader.name;
    return (
      <Typography
        component="div"
        variant="body1"
        sx={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: "2",
          overflow: "hidden",
        }}
      >
        {isTeacherAssigned
          ? t("Taught by: {{sectionLeaderName}}", { sectionLeaderName })
          : t("Teacher not yet assigned")}
      </Typography>
    );
  } else {
    return null;
  }
}

function Seats({
  section,
  t,
}: {
  section: ClassDetailsQuery_SectionFragment;
  t: TFunction;
}) {
  const numOpenings = section.size_max
    ? section.size_max - section.filledSpaceCount
    : 0;
  const showNumOpenings = numOpenings > 0 && numOpenings <= 3;
  const showNumSeatsFilled = numOpenings > 0;

  return (
    <Box flex sx={{ flexDirection: "column" }}>
      {section.size_max == 1 ? null : (
        <>
          {showNumSeatsFilled && (
            <Typography
              component="div"
              variant="body2"
              sx={{
                whiteSpace: "nowrap",
                marginTop: "0.5em",
              }}
            >
              {t("{{numSeatsFilled}} seat filled", {
                numSeatsFilled: section.filledSpaceCount,
                count: section.filledSpaceCount,
                defaultValue_plural: "{{numSeatsFilled}} seats filled",
              })}
            </Typography>
          )}
          {showNumOpenings && (
            <Typography
              component="div"
              variant="body2"
              sx={{
                color: "error.700",
                whiteSpace: "nowrap",
              }}
            >
              {t("Only {{numOpenings}} seat left!", {
                numOpenings,
                count: numOpenings,
                defaultValue_plural: "Only {{numOpenings}} seats left!",
              })}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

function CTARow({
  CtaComponent,
  section,
}: {
  CtaComponent: SectionTableCta;
  section: ClassDetailsQuery_SectionFragment;
}) {
  return (
    <TableRow
      sx={{
        td: { px: "0.25em", py: "0.5em" },
        borderBottom: "1px solid",
        borderBottomColor: "neutral.200",
      }}
    >
      <TableCell>
        <CtaComponent section={section} size="small" />
      </TableCell>
    </TableRow>
  );
}
